import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { SxProps, Theme } from '@mui/material';
import {
  Box, Button, Popover, Checkbox, FormControlLabel, Typography, Stack, Chip, Divider,
} from '@mui/material';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

/**
 * Reusable column-pinning for the community MUI DataGrid (which has no native
 * `pinnedColumns` — that's a Pro feature). Drop it into ANY DataGrid table:
 *
 *   const pin = useColumnPinning({ storageKey: 'x', columns, defaultPinned: ['name'] });
 *   <PinColumnsButton {...pin.buttonProps} />
 *   <Box ref={pin.gridRef}><DataGrid columns={pin.orderedColumns} sx={pin.pinnedSx} …/></Box>
 *
 * How it works: pinned fields are reordered to the front, then frozen at the
 * left with sticky CSS. Left offsets are measured from the live DOM, so both
 * fixed-width and flex columns pin correctly. The selection persists per
 * `storageKey`. Max 4 by default (a pin bar wider than that stops being a
 * "quick reference" and starts eating the scroll area).
 */

const CHECK_FIELD = '__check__';   // MUI's checkbox-selection column field id

function cssEscape(s: string): string {
  try { return (window.CSS && CSS.escape) ? CSS.escape(s) : s; }
  catch { return s; }
}

function sameOffsets(a: Record<string, number>, b: Record<string, number>): boolean {
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

function readStored(key: string, max: number): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.every((x) => typeof x === 'string')) return arr.slice(0, max);
  } catch { /* ignore */ }
  return null;
}

export type PinColumnsButtonProps = {
  columns: { field: string; label: string }[];
  pinned: string[];
  onToggle: (field: string) => void;
  onClear?: () => void;
  max?: number;
};

export function useColumnPinning<R extends Record<string, unknown> = Record<string, unknown>>({
  storageKey,
  columns,
  defaultPinned = [],
  max = 4,
}: {
  storageKey: string;
  columns: GridColDef<R>[];
  defaultPinned?: string[];
  max?: number;
}) {
  const [pinned, setPinned] = useState<string[]>(
    () => readStored(storageKey, max) ?? defaultPinned.slice(0, max),
  );

  const persist = useCallback((next: string[]) => {
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [storageKey]);

  const togglePin = useCallback((field: string) => {
    setPinned((prev) => {
      const next = prev.includes(field)
        ? prev.filter((f) => f !== field)
        : prev.length >= max ? prev : [...prev, field];
      persist(next);
      return next;
    });
  }, [max, persist]);

  const clearPins = useCallback(() => { setPinned([]); persist([]); }, [persist]);

  // Ignore any stored pin whose column no longer exists (dynamic columns).
  const validPinned = useMemo(
    () => pinned.filter((f) => columns.some((c) => c.field === f)),
    [pinned, columns],
  );

  // Pinned fields move to the front (in pin order); the rest keep their order.
  const orderedColumns = useMemo(() => {
    if (!validPinned.length) return columns;
    const byField = new Map(columns.map((c) => [c.field, c]));
    const head = validPinned.map((f) => byField.get(f)).filter(Boolean) as GridColDef<R>[];
    const pinnedSet = new Set(validPinned);
    const tail = columns.filter((c) => !pinnedSet.has(c.field));
    return [...head, ...tail];
  }, [columns, validPinned]);

  // Measure cumulative left offsets from the rendered header cells so any
  // width — fixed or flex — freezes at the correct x.
  const gridRef = useRef<HTMLDivElement>(null);
  const [offsets, setOffsets] = useState<Record<string, number>>({});

  useLayoutEffect(() => {
    const root = gridRef.current;
    if (!root) return;
    const measure = () => {
      let off = 0;
      const cb = root.querySelector(
        `.MuiDataGrid-columnHeader[data-field="${CHECK_FIELD}"]`,
      ) as HTMLElement | null;
      if (cb) off = cb.offsetWidth;
      const next: Record<string, number> = {};
      for (const f of validPinned) {
        next[f] = off;
        const el = root.querySelector(
          `.MuiDataGrid-columnHeader[data-field="${cssEscape(f)}"]`,
        ) as HTMLElement | null;
        off += el ? el.offsetWidth : 140;
      }
      setOffsets((prev) => (sameOffsets(prev, next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [validPinned]);

  const pinnedSx: SxProps<Theme> = useMemo(() => {
    if (!validPinned.length) return {};
    const sx: Record<string, unknown> = {
      // Keep the checkbox-selection column pinned too, so pinned data columns
      // don't slide under it during horizontal scroll.
      [`& .MuiDataGrid-columnHeader[data-field="${CHECK_FIELD}"]`]: {
        position: 'sticky', left: 0, zIndex: 5,
        backgroundColor: 'var(--DataGrid-containerBackground)',
      },
      [`& .MuiDataGrid-cell[data-field="${CHECK_FIELD}"]`]: {
        position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'background.paper',
      },
    };
    validPinned.forEach((f, i) => {
      const left = offsets[f] ?? 0;
      const last = i === validPinned.length - 1;
      // A right-edge divider on the last pinned column marks the frozen band.
      const edge = last ? { borderRight: 1, borderRightColor: 'divider' } : {};
      sx[`& .MuiDataGrid-columnHeader[data-field="${f}"]`] = {
        position: 'sticky', left, zIndex: 3,
        backgroundColor: 'var(--DataGrid-containerBackground)', ...edge,
      };
      sx[`& .MuiDataGrid-cell[data-field="${f}"]`] = {
        position: 'sticky', left, zIndex: 2, backgroundColor: 'background.paper', ...edge,
      };
    });
    // Frozen cells are opaque, so a hovered row's tint can't show through them.
    // Repaint the pinned + checkbox cells with a matching opaque hover tint so
    // the whole row still highlights as one.
    const hoverSel = [CHECK_FIELD, ...validPinned]
      .map((f) => `& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="${f}"]`)
      .join(', ');
    sx[hoverSel] = { backgroundColor: '#f6f9fd' };
    return sx as SxProps<Theme>;
  }, [validPinned, offsets]);

  return {
    pinned: validPinned,
    togglePin,
    clearPins,
    orderedColumns,
    pinnedSx,
    gridRef,
    max,
    /**
     * Spread onto the <DataGrid>. Column virtualization UNMOUNTS off-screen
     * columns mid-scroll (even with a huge columnBufferPx), so a pinned column
     * scrolled to the left edge briefly vanishes until scroll settles and it
     * remounts. Disabling virtualization keeps every cell in the DOM, so frozen
     * columns stay put. Fine here — these grids are server-paginated (≤200/pg).
     */
    gridProps: { disableVirtualization: true } as const,
    /** Spread straight into <PinColumnsButton> — labels come from the caller. */
    buttonProps: (labelled: { field: string; label: string }[]): PinColumnsButtonProps => ({
      columns: labelled, pinned: validPinned, onToggle: togglePin, onClear: clearPins, max,
    }),
  };
}

/** The "Pin columns" button + its checkbox popover. */
export function PinColumnsButton({
  columns, pinned, onToggle, onClear, max = 4,
}: PinColumnsButtonProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const full = pinned.length >= max;
  return (
    <>
      <Button
        variant="outlined" size="small"
        startIcon={<PushPinOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={pinned.length ? { borderColor: 'primary.main', color: 'primary.main' } : undefined}
      >
        Pin columns{pinned.length ? ` · ${pinned.length}` : ''}
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: {
          mt: 0.5, width: 290, borderRadius: 2,
          boxShadow: '0 10px 30px rgba(24,40,72,0.16)', border: '1px solid #eceff3',
        } } }}
      >
        <Box sx={{ p: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Pin columns</Typography>
            <Chip size="small" variant="outlined"
              color={pinned.length ? 'primary' : 'default'} label={`${pinned.length} / ${max}`} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Pinned columns stay fixed on the left while you scroll sideways. Up to {max}.
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          <Box sx={{ maxHeight: 300, overflowY: 'auto', mx: -0.5 }}>
            {columns.map((c) => {
              const checked = pinned.includes(c.field);
              const disabled = !checked && full;
              return (
                <FormControlLabel
                  key={c.field}
                  control={
                    <Checkbox size="small" checked={checked} disabled={disabled}
                      onChange={() => onToggle(c.field)} />
                  }
                  label={<Typography sx={{ fontSize: 13 }}>{c.label}</Typography>}
                  sx={{
                    display: 'flex', width: '100%', m: 0, px: 0.5, py: 0.1, borderRadius: 1,
                    opacity: disabled ? 0.45 : 1,
                    '&:hover': { bgcolor: disabled ? 'transparent' : 'grey.100' },
                  }}
                />
              );
            })}
          </Box>
          {onClear && pinned.length > 0 && (
            <>
              <Divider sx={{ mt: 0.5, mb: 0.25 }} />
              <Button size="small" onClick={onClear} sx={{ fontSize: 12 }}>Unpin all</Button>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
